if ( typeof AnnouncementBar !== 'function' ) {

  class AnnouncementBar extends HTMLElement {

    constructor() {
      super();

      this._prefix = window.KT_PREFIX || '';

      this.slider = this.querySelector('[data-js-slider]');
      this.content = Array.from(this.querySelectorAll(`.${this._prefix}announcement`));
      this.nextButton = this.querySelector(`.${this._prefix}announcement-bar__content-nav--right`);
      this.prevButton = this.querySelector(`.${this._prefix}announcement-bar__content-nav--left`);

      this.length = this.content.length;
      this.index = 0;

      this.mode = this.dataset.rotate || 'slide';                 // manual | slide | fade
      this.isFade = this.mode === 'fade';
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.mode !== 'manual';
      this.autoplayIntervalDelay = this.dataset.autoplayInterval
        ? parseInt(this.dataset.autoplayInterval, 10)
        : 5000;
      this._autoplayInterval = null;

      this.nextButton?.classList.remove(`${this._prefix}announcement-bar__content-nav--disabled`);
      this.prevButton?.classList.remove(`${this._prefix}announcement-bar__content-nav--disabled`);

      if ( this.dataset.randomize === 'true' && this.length > 1 ) {
        this.index = Math.floor(Math.random() * this.length);
      }
      this.applyActive(true);

      if ( this.nextButton ) {
        this.nextButton.addEventListener('click', () => { this.changeSlide('next'); this.restartAutoplay(); });
      }
      if ( this.prevButton ) {
        this.prevButton.addEventListener('click', () => { this.changeSlide('prev'); this.restartAutoplay(); });
      }

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.restartAutoplay());

      if ( this.autoplayEnabled && this.length > 1 ) {
        this.startAutoplay();
      }

      // bright-lights effect (starfield + cursor spotlight), ported from the Crest
      if ( this.dataset.stars === 'true' ) this.initStars();
    }

    startAutoplay() {
      this.stopAutoplay();
      if ( !this.autoplayEnabled || this.length < 2 ) return;
      this._autoplayInterval = setInterval(() => this.changeSlide('next'), this.autoplayIntervalDelay);
    }

    stopAutoplay() {
      if ( this._autoplayInterval ) { clearInterval(this._autoplayInterval); this._autoplayInterval = null; }
    }

    restartAutoplay() { if ( this.autoplayEnabled ) this.startAutoplay(); }

    changeSlide(direction) {
      if ( !this.length ) return;
      if ( direction === 'next' )      this.index = (this.index + 1) % this.length;
      else if ( direction === 'prev' ) this.index = (this.index - 1 + this.length) % this.length;
      this.applyActive(false);
    }

    applyActive(instant) {
      if ( this.isFade ) {
        this.content.forEach((el, i) => {
          if ( instant ) el.style.transition = 'none';
          el.classList.toggle('is-active', i === this.index);
        });
        if ( instant ) {
          void this.offsetWidth;
          this.content.forEach((el) => { el.style.transition = ''; });
        }
      } else if ( this.slider && this.content[this.index] ) {
        this.slider.scrollTo({ top: 0, left: this.content[this.index].offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      }
    }

    initStars() {
      // clip layer so both the starfield and the spotlight stay inside the bar
      const fx = document.createElement('div');
      fx.className = 'ck-ab__fx';
      fx.setAttribute('aria-hidden', 'true');
      this.insertBefore(fx, this.firstChild);

      const canvas = document.createElement('canvas');
      canvas.className = 'ck-ab__stars';
      fx.appendChild(canvas);

      const ctx = canvas.getContext('2d');
      if ( !ctx ) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let W = 0, H = 0, stars = [];

      const seed = () => {
        stars = [];
        const count = Math.max(24, Math.floor(W / 16));
        for ( let i = 0; i < count; i++ ) {
          stars.push({
            x: Math.random() * W, y: Math.random() * H,
            r: Math.random() * 1.5 + 0.5,
            vx: (Math.random() - 0.5) * 0.14, vy: (Math.random() - 0.5) * 0.06,
            base: Math.random() * 0.45 + 0.45,
            tw: Math.random() * Math.PI * 2, tws: Math.random() * 0.045 + 0.012
          });
        }
      };

      const resize = () => {
        const r = this.getBoundingClientRect();
        W = r.width; H = r.height;
        canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
        canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        seed();
      };

      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        for ( const p of stars ) {
          const o = p.base * (0.6 + 0.4 * Math.sin(p.tw));
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          if ( p.r > 1.1 ) { ctx.shadowBlur = 7; ctx.shadowColor = 'rgba(255,236,200,' + o.toFixed(3) + ')'; }
          else { ctx.shadowBlur = 0; }
          ctx.fillStyle = 'rgba(255,246,224,' + o.toFixed(3) + ')';
          ctx.fill();
        }
        ctx.shadowBlur = 0;
      };

      const tick = () => {
        for ( const p of stars ) {
          p.x += p.vx; p.y += p.vy; p.tw += p.tws;
          if ( p.x < -2 ) p.x = W + 2;
          if ( p.x > W + 2 ) p.x = -2;
          if ( p.y < -2 ) p.y = H + 2;
          if ( p.y > H + 2 ) p.y = -2;
        }
        draw();
        this._starRAF = requestAnimationFrame(tick);
      };

      resize();
      window.addEventListener('resize', resize);

      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if ( reduce ) { draw(); return; }
      tick();

      // cursor-following spotlight (same idea as the Crest)
      const spot = document.createElement('div');
      spot.className = 'ck-ab__spot';
      fx.appendChild(spot);
      let px = 0, py = 0, pending = false;
      this.addEventListener('mouseenter', () => this.classList.add('is-spot-on'));
      this.addEventListener('mouseleave', () => this.classList.remove('is-spot-on'));
      this.addEventListener('mousemove', (e) => {
        const r = this.getBoundingClientRect();
        px = e.clientX - r.left; py = e.clientY - r.top;
        if ( !pending ) {
          pending = true;
          requestAnimationFrame(() => { spot.style.left = px + 'px'; spot.style.top = py + 'px'; pending = false; });
        }
      });
    }

  }

  if ( typeof customElements.get('announcement-bar') === 'undefined' ) {
    customElements.define('announcement-bar', AnnouncementBar);
  }

}
